import React, { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import './Sidebar.css';

const Sidebar = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user && user.roles && user.roles.includes('ROLE_ADMIN');

  const [expandedGroups, setExpandedGroups] = useState({ alpaca: false });

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Tableau de bord',
      icon: '📊',
      path: '/dashboard',
      requiresAdmin: false
    },
    {
      id: 'users',
      label: 'Utilisateurs',
      icon: '👥',
      path: '/users',
      requiresAdmin: true
    },
    {
      id: 'alpaca',
      label: 'Alpaca',
      icon: '🦙',
      requiresAdmin: false,
      children: [
        {
          id: 'alpaca-config',
          label: 'Configuration',
          icon: '⚙️',
          path: '/alpaca/config',
          requiresAdmin: false
        },
        {
          id: 'alpaca-bot',
          label: 'Bot de Trading',
          icon: '🤖',
          path: '/alpaca/bot',
          requiresAdmin: false
        },
        {
          id: 'alpaca-portfolio',
          label: 'Portefeuille',
          icon: '💼',
          path: '/alpaca/portfolio',
          requiresAdmin: true
        },
        {
          id: 'alpaca-assets',
          label: 'Actions',
          icon: '📈',
          path: '/alpaca/assets',
          requiresAdmin: true
        },
        {
          id: 'alpaca-history-check',
          label: 'Vérification historique',
          icon: '📊',
          path: '/alpaca/history-check',
          requiresAdmin: true
        },
        {
          id: 'alpaca-top-gainers',
          label: 'Top Gainers',
          icon: '🚀',
          path: '/alpaca/top-gainers',
          requiresAdmin: true
        },
        {
          id: 'alpaca-ai-signals',
          label: 'Signaux IA',
          icon: '🧠',
          path: '/alpaca/ai-signals',
          requiresAdmin: true
        },
        {
          id: 'bot-logs',
          label: 'Logs des bots',
          icon: '📋',
          path: '/bot-logs',
          requiresAdmin: true
        },
        {
          id: 'alpaca-symbol-chart',
          label: 'Graphique d\'actif',
          icon: '📊',
          path: '/alpaca/symbol-chart',
          requiresAdmin: false
        }
      ]
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: '🔔',
      requiresAdmin: true,
      children: [
        {
          id: 'notifications-telegram',
          label: 'Telegram',
          icon: '✉️',
          path: '/notifications/telegram'
        }
      ]
    },
    {
      id: 'emails',
      label: 'Messagerie',
      icon: '📧',
      path: '/emails',
      requiresAdmin: false
    },
    {
      id: 'crons',
      label: 'Tâches planifiées',
      icon: '⏱️',
      path: '/crons',
      requiresAdmin: true
    },
    {
      id: 'ollama-chat',
      label: 'Chat IA',
      icon: '💬',
      path: '/ollama-chat',
      requiresAdmin: false
    },
    {
      id: 'settings',
      label: 'Paramètres',
      icon: '⚙️',
      path: '/settings',
      requiresAdmin: false
    }
  ].filter(item => !item.requiresAdmin || isAdmin);

  return (
    <div className={`sidebar ${isOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">
          <span className="logo-icon">🏢</span>
          {isOpen && <span className="logo-text">Technonet</span>}
        </div>
        <button className="sidebar-toggle" onClick={onToggle}>
          {isOpen ? '←' : '→'}
        </button>
      </div>

      <nav className="sidebar-nav">
        <ul className="nav-list">
          {menuItems.map((item) => {
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const isGroupActive = hasChildren && item.children.some((child) => location.pathname.startsWith(child.path));
            const isExpanded = hasChildren ? !!expandedGroups[item.id] : false;

            if (!hasChildren) {
              return (
                <li key={item.id} className="nav-item">
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    {isOpen && <span className="nav-label">{item.label}</span>}
                  </NavLink>
                </li>
              );
            }

            return (
              <li key={item.id} className={`nav-item nav-group ${isExpanded ? 'expanded' : ''}`}>
                <button
                  type="button"
                  className={`nav-link nav-group-toggle ${isGroupActive ? 'active' : ''}`}
                  onClick={() => setExpandedGroups({ ...expandedGroups, [item.id]: !isExpanded })}
                >
                  <span className="nav-icon">{item.icon}</span>
                  {isOpen && <span className="nav-label">{item.label}</span>}
                  {isOpen && (
                    <span className={`nav-caret ${isExpanded ? 'rotated' : ''}`}>▸</span>
                  )}
                </button>
                <ul className={`sub-list ${isExpanded && isOpen ? 'open' : ''}`}>
                  {item.children
                    .filter(child => !child.requiresAdmin || isAdmin)
                    .map((child) => (
                      <li key={child.id} className="nav-item sub-item">
                        <NavLink
                          to={child.path}
                          className={({ isActive }) => `nav-link nav-sublink ${isActive ? 'active' : ''}`}
                        >
                          <span className="nav-icon">{child.icon}</span>
                          {isOpen && <span className="nav-label">{child.label}</span>}
                        </NavLink>
                      </li>
                    ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;
